import { set, child, remove, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'
import {
  type Chyron,
  type ChyronDTO,
  ChyronType,
  mapDTOToChyron,
} from '../model/chyron-model'

const ROOT_NODE = 'overlays'

const overlayState = proxy<{ overlay: Chyron | null }>({
  overlay: null,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener to track the overlays updates.
  onValue(child(connection.room, ROOT_NODE), (snapshot) => {
    const val = snapshot.val()
    overlayState.overlay = val === null ? null : mapDTOToChyron(val)
  })
})

// Actions

/**
 * Sets the overlay.
 * @param overlay - The overlay to be set.
 */
const changeOverlay = async (overlay: ChyronDTO) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODE), overlay).catch(
    (err: unknown) => {
      connection.logError('SET_OVERLAY', 'Could not set overlay', err)
    }
  )
}

/**
 * Removes the overlay.
 */
const removeOverlay = async () => {
  const connection = await firebaseConnection.get()

  return remove(child(connection.room, ROOT_NODE)).catch((err: unknown) => {
    connection.logError('SET_OVERLAY', 'Could not remove overlay', err)
  })
}

/**
 * Adds a new chyron or deletes it if null is passed.
 * @param chyron - The chyron to be set.
 */
const setChyron = async (chyron: Chyron | null) => {
  const connection = await firebaseConnection.get()

  try {
    if (chyron === null) {
      await remove(child(connection.room, ROOT_NODE))
    } else {
      const { type, ...data } = chyron
      await set(child(connection.room, ROOT_NODE), {
        type,

        // Firebase database doesn't like undefined fields.
        // This is the "cleanest" way to clean the arbitrary data for undefined fields,
        // without going crazy with mapping back and forth between objects and DTOs
        data: JSON.parse(JSON.stringify(data)),
      })
    }
  } catch (err) {
    connection.logError('SET_OVERLAY', 'Failed to set the overlay', err)
  }
}

/**
 * Adds an info chyron
 * @param title - The title of the chyron.
 * @param epoch - The epoch of the chyron.
 * @param thumbnail - The thumbnail of the chyron.
 */
const setInfoChyron = (title: string, epoch: string, thumbnail: string) => {
  const date = (+epoch * 1000) as unknown
  return setChyron({
    type: ChyronType.WebinarInfo,
    title,
    date: date as Date,
    thumbnail,
  })
}

/** Adds a speaker info chyron */
const setSpeakerInfoChyron = (params: {
  id: number
  fullName: string
  title?: string
  avatarURL?: string
  bio?: string
  company?: string
}) => {
  return setChyron({
    type: ChyronType.SpeakerInfo,
    liveSpeakerId: params.id,
    name: params.fullName,
    title: params.title,
    avatarUrl: params.avatarURL,
    bio: params.bio,
    company: params.company,
  })
}

const useOverlayState = () => useSnapshot(overlayState).overlay

const getOverlayState = () => overlayState.overlay

export {
  useOverlayState,
  changeOverlay,
  removeOverlay,
  overlayState,
  getOverlayState,
  setChyron,
  setInfoChyron,
  setSpeakerInfoChyron,
}
